/**
 * O editor da ata — a tela 7, e o lugar onde a fronteira desta fatia é atravessada de propósito.
 *
 * Cinco decisões:
 *
 * · **A ata escrita à mão é o caminho completo, não o de emergência.** Não há credencial do
 *   `geo-ai-gateway` e `ffmpeg` não está no container: o resumo digitado aqui é o que publica a
 *   ata hoje, e nada nesta tela trata `transcricao_midia_id` nulo como pendência. Abrir a ata não
 *   pede mídia nenhuma.
 *
 * · **Publicar é o ato, e por isso tem dois passos.** Antes de publicar, a ata não existe para o
 *   cliente de forma alguma — não é a consulta que a esconde, é a policy da `0013`. Depois, o
 *   conteúdo não se reescreve: o servidor recusa com `409`, porque trocar em silêncio o texto que
 *   o cliente já leu é pior que o erro de digitação que a edição consertaria. O segundo passo
 *   conta, com números, quantos itens atravessam.
 *
 * · **A prévia se chama prévia.** Quem recorta é `visibilidade.py`, e antes dele a policy do
 *   banco. Uma caixa apresentada como "o que o cliente vê" viraria, na cabeça de quem usa, a
 *   garantia que ela não é — e no dia em que a tela errasse, ninguém desconfiaria dela.
 *
 * · **`ocultos` é dito, e não escondido.** Um interno sem `ata:ver_interno` lê a ata com buracos;
 *   omitir o contador faria a lista parecer completa. Para o cliente o servidor manda sempre `0`,
 *   pelo motivo oposto: um contador de itens ocultos é a própria informação que a fronteira
 *   existe para não entregar.
 *
 * · **Pendência do cliente que não atravessa não vira cobrança**, e a publicação conta isso antes
 *   de acontecer. É o defeito mais silencioso da fatia: sem o aviso, quem publica acha que cobrou.
 */

import { useState } from "react";
import { Aviso, Botao, CampoLongo, Cartao, Chip, GrupoDeOpcoes, Opcao } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  MAXIMO_DO_RESUMO,
  ROTULO_DO_TIPO_DE_ITEM,
  contarAFronteira,
  destinoDoItem,
  formatarQuando,
  useAbrirAta,
  useEditarAta,
  usePublicarAta,
  useTarefasTreinamento,
  type Ata,
  type ItemDaAta,
  type Midia,
} from "../../nucleo/reuniao";
import { ItemNaAta } from "./ItemNaAta";
import { FormularioDoItem } from "./FormularioDoItem";
import { MarcaDeDestino } from "./MarcaDeDestino";

export interface PropsDoEditorDaAta {
  reuniaoId: string;
  tipo?: string;
  /** O nome do cliente. Entra na confirmação: publicar a ata do cliente errado é caro e mudo. */
  cliente: string;
  /** A ata **já recortada pelo servidor** para esta sessão, ou `null` quando não há ata. */
  ata: Ata | null;
  /** As transcrições concluídas desta reunião — vazio quando a sessão não alcança a mídia. */
  transcricoes: readonly Midia[];
  podeEditar: boolean;
  podePublicar: boolean;
  /** `ata:ver_interno` — quem escreve nota interna, e quem enxerga as que já existem. */
  veNotaInterna: boolean;
  /**
   * A reunião já foi registrada como realizada. **O servidor recusa publicar antes disso** — a
   * ata é o registro do que foi dito, e publicá-la antes faria a esteira do cliente contar a
   * história fora de ordem (`ATA_PUBLICADA` antes de `REUNIAO_REALIZADA`). A tela diz o que falta
   * em vez de deixar o consultor descobrir pelo `409`.
   */
  reuniaoRealizada: boolean;
}

export function EditorDaAta({
  reuniaoId,
  tipo = "kickoff",
  cliente,
  ata,
  transcricoes,
  podeEditar,
  podePublicar,
  veNotaInterna,
  reuniaoRealizada,
}: PropsDoEditorDaAta) {
  if (ata === null) {
    return <AtaAusente reuniaoId={reuniaoId} podeEditar={podeEditar} />;
  }
  return (
    <AtaEmTela
      ata={ata}
      tipo={tipo}
      cliente={cliente}
      transcricoes={transcricoes}
      podeEditar={podeEditar}
      podePublicar={podePublicar}
      veNotaInterna={veNotaInterna}
      reuniaoRealizada={reuniaoRealizada}
    />
  );
}

function AtaAusente({ reuniaoId, podeEditar }: { reuniaoId: string; podeEditar: boolean }) {
  const abrir = useAbrirAta(reuniaoId);

  if (!podeEditar) {
    return (
      <Cartao titulo="Ainda não há ata publicada" data-testid="ata-ausente">
        <p className="cartao__texto">
          Quando a ata desta reunião for publicada, ela aparece aqui inteira: o resumo, as decisões
          e as pendências, com responsável e prazo.
        </p>
        <p className="nota-rodape mt-2">
          Quem escreve e publica a ata é a consultoria.
        </p>
      </Cartao>
    );
  }

  return (
    <Cartao titulo="Nenhuma ata aberta para esta reunião" data-testid="ata-ausente">
      <p className="cartao__texto">
        <b>Escrever a ata à mão funciona inteiro e sozinho.</b> Abrir o rascunho não pede gravação,
        áudio nem transcrição — a ata sem mídia nenhuma é o caminho normal desta fatia, e é o que
        publica hoje. Enquanto o rascunho existir, o cliente não vê nada: para a sessão dele, uma
        ata em rascunho não existe.
      </p>
      {abrir.isError ? (
        <Aviso tom="erro" className="mt-3" data-testid="erro-de-abrir-ata">
          {mensagemDoConsole(abrir.error)}
        </Aviso>
      ) : null}
      <div className="acoes-do-console mt-3">
        <Botao
          disabled={abrir.isPending}
          onClick={() => abrir.mutate({})}
          data-testid="abrir-ata"
        >
          {abrir.isPending ? "Abrindo…" : "Abrir o rascunho da ata"}
        </Botao>
      </div>
    </Cartao>
  );
}

function AtaEmTela({
  ata,
  tipo,
  cliente,
  transcricoes,
  podeEditar,
  podePublicar,
  veNotaInterna,
  reuniaoRealizada,
}: {
  ata: Ata;
  tipo: string;
  cliente: string;
  transcricoes: readonly Midia[];
  podeEditar: boolean;
  podePublicar: boolean;
  veNotaInterna: boolean;
  reuniaoRealizada: boolean;
}) {
  const [acrescentando, setAcrescentando] = useState(false);
  const contagem = contarAFronteira(ata.itens);
  const emRascunho = !ata.publicada;

  return (
    <>
      <Cartao
        titulo={ata.publicada ? "Ata publicada" : "Rascunho da ata"}
        data-testid="ata-da-reuniao"
        /* O estado do DOCUMENTO no cartão, e o chip com o próprio estado de cor: pendurar os dois
           no mesmo elemento faria "rascunho" e "aguardando" disputarem o mesmo atributo. */
        data-estado={ata.estado}
        acessorio={
          <Chip estado={ata.publicada ? "ok" : "aguardando"} data-testid="estado-da-ata">
            {ata.publicada ? "o cliente já lê" : "invisível ao cliente"}
          </Chip>
        }
      >
        {emRascunho ? (
          <p className="cartao__texto">
            Enquanto estiver em rascunho, <b>nada disto existe para o cliente</b> — nem o resumo,
            nem um item marcado como visível. O cliente só vê a ata depois de publicada.
          </p>
        ) : (
          <p className="cartao__texto">
            Publicada em{" "}
            <b className="numero-tabular">{formatarQuando(ata.publicado_em) ?? "—"}</b>
            {ata.publicado_por_email ? ` por ${ata.publicado_por_email}` : ""}. O conteúdo de uma
            ata publicada não é reescrito: o cliente pode já tê-la lido, e um texto que muda
            sozinho é pior que o erro que a correção consertaria.
          </p>
        )}

        <ResumoDaAta ata={ata} podeEditar={podeEditar} />

        {transcricoes.length > 0 && emRascunho && podeEditar ? (
          <OrigemDoResumo ata={ata} transcricoes={transcricoes} />
        ) : null}
      </Cartao>

      <section className="mt-4" aria-labelledby="titulo-dos-itens-da-ata">
        <h3 className="titulo-secao" id="titulo-dos-itens-da-ata">
          Decisões, pendências e notas
        </h3>

        <p className="lead mb-3" data-testid="contagem-da-fronteira">
          {ata.itens.length === 0 ? (
            "Nenhum item registrado ainda."
          ) : (
            <>
              <b>{contagem.vaoAoCliente}</b> {contagem.vaoAoCliente === 1 ? "item vai" : "itens vão"}{" "}
              ao cliente · <b>{contagem.ficamDentro}</b>{" "}
              {contagem.ficamDentro === 1 ? "fica" : "ficam"} só aqui dentro
              {contagem.notasInternas > 0 ? (
                <>
                  {" "}
                  (<b>{contagem.notasInternas}</b>{" "}
                  {contagem.notasInternas === 1 ? "nota interna" : "notas internas"})
                </>
              ) : null}
              .
            </>
          )}
        </p>

        {ata.ocultos > 0 ? (
          <Aviso tom="marca" className="mb-3" data-testid="itens-ocultos">
            <b>
              Há {ata.ocultos} {ata.ocultos === 1 ? "item" : "itens"} nesta ata que este acesso não
              enxerga.
            </b>{" "}
            São as notas internas, que o seu acesso não mostra. A lista abaixo está incompleta.
          </Aviso>
        ) : null}

        {ata.itens.length > 0 ? (
          <div className="lista-de-itens" data-testid="itens-da-ata">
            {ata.itens.map((item) => (
              <ItemNaAta
                key={item.id}
                ata={ata}
                item={item}
                podeEditar={podeEditar}
                podeEscreverNotaInterna={veNotaInterna}
              />
            ))}
          </div>
        ) : null}

        {podeEditar && emRascunho ? (
          <div className="mt-3">
            {acrescentando ? (
              <FormularioDoItem
                ataId={ata.id}
                podeEscreverNotaInterna={veNotaInterna}
                aoFechar={() => setAcrescentando(false)}
              />
            ) : (
              <Botao
                variante="fantasma"
                onClick={() => setAcrescentando(true)}
                data-testid="acrescentar-item"
              >
                Acrescentar item
              </Botao>
            )}
          </div>
        ) : null}

        {emRascunho ? <PreviaDoCliente itens={ata.itens} /> : null}
      </section>

      {emRascunho ? (
        <Publicacao
          ata={ata}
          tipo={tipo}
          cliente={cliente}
          podePublicar={podePublicar}
          contagem={contagem}
          reuniaoRealizada={reuniaoRealizada}
        />
      ) : null}
    </>
  );
}

/** O corpo que o cliente lê. Sem ele o servidor recusa publicar — página em branco solene. */
function ResumoDaAta({ ata, podeEditar }: { ata: Ata; podeEditar: boolean }) {
  const [resumo, setResumo] = useState(ata.resumo ?? "");
  const editar = useEditarAta(ata.id);
  const mudou = resumo !== (ata.resumo ?? "");

  if (!podeEditar || ata.publicada) {
    return (
      <div className="mt-3">
        <p className="campo__rotulo">Resumo</p>
        <p className="ata-publicada__resumo" data-testid="resumo-da-ata">
          {ata.resumo ?? "— (ainda sem resumo)"}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <CampoLongo
        rotulo="Resumo da reunião"
        rows={6}
        value={resumo}
        maxLength={MAXIMO_DO_RESUMO}
        onChange={(evento) => setResumo(evento.target.value)}
        ajuda="É o corpo que o cliente lê quando a ata for publicada. Escreva com as palavras que você usaria falando com ele."
        data-testid="campo-do-resumo"
      />
      {editar.isError ? (
        <Aviso tom="erro" className="mb-3" data-testid="erro-do-resumo">
          {mensagemDoConsole(editar.error)}
        </Aviso>
      ) : null}
      <div className="acoes-do-console">
        <Botao
          tamanho="pequeno"
          disabled={editar.isPending || !mudou}
          onClick={() => editar.mutate({ resumo })}
          data-testid="salvar-resumo"
        >
          {editar.isPending ? "Salvando…" : "Salvar rascunho"}
        </Botao>
        {mudou ? <span className="nota-rodape">Há alteração não salva.</span> : null}
      </div>
    </div>
  );
}

/**
 * De qual transcrição este resumo saiu — quando saiu de alguma.
 *
 * O vínculo existe para quem for reler discordando de uma frase achar o material de origem. Ele é
 * opcional em todos os sentidos: `transcricao_midia_id` nulo é o estado normal, e a ata escrita à
 * mão não é uma ata pela metade.
 */
function OrigemDoResumo({ ata, transcricoes }: { ata: Ata; transcricoes: readonly Midia[] }) {
  const editar = useEditarAta(ata.id);

  return (
    <GrupoDeOpcoes rotulo="Transcrição de origem (opcional)" className="mt-4">
      <Opcao
        tipo="radio"
        name={`transcricao-${ata.id}`}
        checked={ata.transcricao_midia_id === null}
        onChange={() => editar.mutate({ limpar_transcricao: true })}
        titulo="Nenhuma — ata escrita à mão"
        descricao="O caminho normal desta fatia. Nada aqui trata a ausência de transcrição como pendência."
        data-testid="sem-transcricao"
      />
      {transcricoes.map((midia) => (
        <Opcao
          key={midia.id}
          tipo="radio"
          name={`transcricao-${ata.id}`}
          checked={ata.transcricao_midia_id === midia.id}
          onChange={() => editar.mutate({ transcricao_midia_id: midia.id })}
          titulo={midia.nome_original ?? "Transcrição"}
          descricao={
            midia.caracteres ? `${midia.caracteres.toLocaleString("pt-BR")} caracteres` : undefined
          }
          data-testid={`transcricao-${midia.id}`}
        />
      ))}
    </GrupoDeOpcoes>
  );
}

/**
 * A prévia do recorte — e ela diz, com todas as letras, que é prévia.
 *
 * O que decide é o servidor. Isto aqui é o mesmo predicado repetido do lado de cá para quem
 * escreve conferir antes de publicar, e a redundância é barata: numa fronteira, um erro que
 * precisa acontecer nos dois lados ao mesmo tempo é mais difícil que um erro que precisa
 * acontecer num lado só.
 */
function PreviaDoCliente({ itens }: { itens: readonly ItemDaAta[] }) {
  const [aberta, setAberta] = useState(false);
  const atravessam = itens.filter((item) => destinoDoItem(item) === "cliente");

  return (
    <div className="previa-do-cliente" data-testid="previa-do-cliente">
      <p className="previa-do-cliente__titulo">Prévia — o que sai daqui quando você publicar</p>
      {aberta ? (
        atravessam.length === 0 ? (
          <p className="previa-do-cliente__vazia">
            Nenhum item atravessa. O cliente vai ler só o resumo.
          </p>
        ) : (
          <ul className="ata-publicada__lista">
            {atravessam.map((item) => (
              <li
                key={item.id}
                className="ata-publicada__item"
                data-tipo={item.tipo}
                data-testid={`previa-${item.id}`}
              >
                {item.texto}
                <span className="ata-publicada__meta">
                  {ROTULO_DO_TIPO_DE_ITEM[item.tipo]}
                  {item.responsavel ? ` · ${item.responsavel}` : ""}
                </span>
              </li>
            ))}
          </ul>
        )
      ) : (
        <p className="previa-do-cliente__vazia">
          {atravessam.length === 0
            ? "Nenhum item atravessa por enquanto."
            : `${atravessam.length} ${atravessam.length === 1 ? "item atravessa" : "itens atravessam"}.`}
        </p>
      )}
      <div className="acoes-do-console mt-2">
        <Botao
          variante="fantasma"
          tamanho="pequeno"
          onClick={() => setAberta((valor) => !valor)}
          data-testid="alternar-previa"
        >
          {aberta ? "Fechar a prévia" : "Ver o que sai"}
        </Botao>
      </div>
      <p className="nota-rodape mt-2">
        Prévia, e só prévia: quem recorta de verdade é o servidor, na mesma decisão que atende o
        cliente. Se as duas discordarem um dia, quem manda é a de lá.
      </p>
    </div>
  );
}

/** Publicar — o ato que faz a ata existir para o cliente. Dois passos, com os números na frente. */
function Publicacao({
  ata,
  tipo,
  cliente,
  podePublicar,
  contagem,
  reuniaoRealizada,
}: {
  ata: Ata;
  tipo: string;
  cliente: string;
  podePublicar: boolean;
  contagem: ReturnType<typeof contarAFronteira>;
  reuniaoRealizada: boolean;
}) {
  const [confirmando, setConfirmando] = useState(false);
  const [selecionadas, setSelecionadas] = useState<string[] | null>(null);
  const publicar = usePublicarAta(ata.id);
  const tarefas = useTarefasTreinamento(ata.id, tipo === "treinamento" && podePublicar);
  const escrita = tarefas.data?.escrita ?? false;
  const marcadas = escrita
    ? selecionadas ?? tarefas.data?.tarefas.map((tarefa) => tarefa.chave) ?? []
    : [];
  const semResumo = (ata.resumo ?? "").trim().length === 0;
  const impedida = semResumo || !reuniaoRealizada ||
    (tipo === "treinamento" && (tarefas.isPending || tarefas.isError));

  if (!podePublicar) {
    return (
      <Aviso tom="marca" className="mt-4" data-testid="sem-permissao-de-publicar">
        <b>Quem publica a ata é a consultoria.</b> A infraestrutura lê a ata inteira e não
        publica.
      </Aviso>
    );
  }

  if (!confirmando) {
    return (
      <Cartao className="mt-4" titulo="Publicar a ata" data-testid="publicacao-da-ata">
        <p className="cartao__texto">
          Publicar é o que faz esta ata <b>existir para o cliente</b>. Depois disso o conteúdo não
          se reescreve — o servidor recusa —, e cada pendência dele que atravessar vira cobrança na
          esteira do cliente.
        </p>
        {semResumo ? (
          <Aviso tom="atencao" className="mt-3" data-testid="resumo-vazio">
            <b>Falta o resumo.</b> Ele é o corpo que o cliente lê; publicar sem ele entregaria uma
            página em branco com a solenidade de uma publicação.
          </Aviso>
        ) : null}
        {!reuniaoRealizada ? (
          <Aviso tom="atencao" className="mt-3" data-testid="reuniao-nao-realizada">
            <b>Registre primeiro que a reunião aconteceu.</b> A ata é o registro do que foi dito, e
            publicá-la antes faria a esteira do cliente mostrar a ata publicada antes da reunião
            realizada. O botão está no bloco da reunião, acima.
          </Aviso>
        ) : null}
        {tipo === "treinamento" ? (
          <section className="mt-3" aria-label="Tarefas do treinamento no GeoTask">
            {tarefas.isPending ? <p role="status">Carregando as tarefas do treinamento…</p> : null}
            {tarefas.isError ? (
              <Aviso tom="erro">
                Não foi possível consultar as tarefas do GeoTask. Tente de novo antes de publicar.
                <Botao variante="fantasma" onClick={() => void tarefas.refetch()}>Tentar de novo</Botao>
              </Aviso>
            ) : null}
            {tarefas.data?.pendencia ? <Aviso tom="atencao">{tarefas.data.pendencia}</Aviso> : null}
            {tarefas.data?.tarefas.length === 0 ? (
              <p className="nota-rodape">Nenhuma tarefa em Treinamento foi encontrada. A ata poderá ser publicada sem movimentar tarefas.</p>
            ) : null}
            {tarefas.data && tarefas.data.tarefas.length > 0 && !escrita ? (
              <Aviso tom="marca" data-testid="geotask-escrita-desligada">
                A escrita no GeoTask está desligada neste ambiente: publicar a ata não move
                estas tarefas nem comenta nelas.
              </Aviso>
            ) : null}
            {tarefas.data && tarefas.data.tarefas.length > 0 && escrita ? (
              <GrupoDeOpcoes rotulo="Tarefas que avançam ao publicar">
                {tarefas.data.tarefas.map((tarefa) => (
                  <Opcao key={tarefa.chave} tipo="checkbox" titulo={tarefa.nome}
                    descricao={tarefa.tipo === "modulo" ? "Módulo → Acompanhamento" : "Recurso Plus → Concluído"}
                    checked={marcadas.includes(tarefa.chave)}
                    onChange={(evento) => setSelecionadas(evento.target.checked
                      ? [...marcadas, tarefa.chave]
                      : marcadas.filter((chave) => chave !== tarefa.chave))} />
                ))}
              </GrupoDeOpcoes>
            ) : null}
          </section>
        ) : null}
        <div className="acoes-do-console mt-3">
          <Botao
            disabled={impedida}
            onClick={() => {
              publicar.reset();
              setConfirmando(true);
            }}
            data-testid="pedir-publicacao"
          >
            Publicar para o cliente
          </Botao>
        </div>
      </Cartao>
    );
  }

  return (
    <Cartao
      className="cartao--grave mt-4"
      titulo="Confirmar a publicação"
      data-testid="confirmacao-da-publicacao"
      acessorio={<Chip estado="atencao">confirmação</Chip>}
    >
      <p className="cartao__texto" role="status">
        <b>
          {contagem.vaoAoCliente} {contagem.vaoAoCliente === 1 ? "item" : "itens"} e o resumo passam
          a ser lidos pelo cliente.
        </b>{" "}
        {contagem.ficamDentro > 0 ? (
          <>
            {contagem.ficamDentro === 1 ? "Outro" : "Outros"} {contagem.ficamDentro}{" "}
            {contagem.ficamDentro === 1 ? "fica" : "ficam"} do lado de dentro
            {contagem.notasInternas > 0
              ? `, incluindo ${contagem.notasInternas} ${
                  contagem.notasInternas === 1 ? "nota interna" : "notas internas"
                }`
              : ""}
            .
          </>
        ) : (
          <>Nenhum item fica do lado de dentro.</>
        )}{" "}
        Quem passa a ler é <b>{cliente}</b>.
      </p>
      {tipo === "treinamento" ? (
        <p className="cartao__texto" data-testid="tarefas-confirmadas-geotask">
          {escrita ? (
            <>
              No GeoTask, avançarão: {tarefas.data?.tarefas.filter((tarefa) => marcadas.includes(tarefa.chave))
                .map((tarefa) => tarefa.nome).join(", ") || "nenhuma tarefa"}. Um comentário interno
              por tarefa incluirá o resumo e as notas internas, sem transcrição.
            </>
          ) : (
            <>No GeoTask, nada muda: a escrita está desligada neste ambiente.</>
          )}
        </p>
      ) : null}

      {/* Os dois números, separados e cada um no próprio elemento. "1 item vai para o cliente, 1
          fica interno" é a frase que faz alguém reler antes de clicar, e o segundo número é o que
          denuncia a nota interna marcada visível por engano — no único momento em que ainda dá
          para desfazer. */}
      <dl className="grid gap-3 sm:grid-cols-2 m-0 mb-3">
        <div>
          <dt className="campo__rotulo">
            <MarcaDeDestino destino="cliente" />
          </dt>
          <dd
            className="m-0 text-[20px] font-bold numero-tabular"
            data-testid="itens-que-o-cliente-vera"
          >
            {contagem.vaoAoCliente}
          </dd>
        </div>
        <div>
          <dt className="campo__rotulo">
            <MarcaDeDestino destino="interno" />
          </dt>
          <dd
            className="m-0 text-[20px] font-bold numero-tabular"
            data-testid="itens-que-ficam-internos"
          >
            {contagem.ficamDentro}
          </dd>
        </div>
      </dl>

      {contagem.pendenciasDoClienteMudas > 0 ? (
        <Aviso tom="atencao" className="mb-3" data-testid="pendencias-mudas">
          <b>
            {contagem.pendenciasDoClienteMudas}{" "}
            {contagem.pendenciasDoClienteMudas === 1
              ? "pendência é do cliente e não vai chegar até ele"
              : "pendências são do cliente e não vão chegar até ele"}
            .
          </b>{" "}
          Elas não viram cobrança na esteira dele — o portal não cobra ninguém por um texto que a
          pessoa não pode ler. Se a intenção era cobrar, volte e marque o item como visível antes
          de publicar.
        </Aviso>
      ) : null}

      <p className="nota-rodape mb-3">
        <b>Não existe despublicar.</b> Nenhuma tela do portal tira a ata do ar, e o conteúdo
        publicado não é reescrito: uma correção vira observação na esteira ou assunto da próxima
        reunião, onde ela aparece como correção e não como um texto que mudou sozinho.
      </p>

      {publicar.isError ? (
        <Aviso tom="erro" className="mb-3" data-testid="erro-da-publicacao">
          {mensagemDoConsole(publicar.error)}
        </Aviso>
      ) : null}

      <div className="acoes-do-console">
        <Botao
          disabled={publicar.isPending}
          onClick={() => publicar.mutate(tipo === "treinamento" && escrita ? marcadas : undefined,
            { onSuccess: () => setConfirmando(false) })}
          data-testid="confirmar-publicacao"
        >
          {publicar.isPending ? "Publicando…" : "Publicar agora"}
        </Botao>
        <Botao
          variante="fantasma"
          disabled={publicar.isPending}
          onClick={() => setConfirmando(false)}
          data-testid="cancelar-publicacao"
        >
          Voltar e revisar
        </Botao>
      </div>
    </Cartao>
  );
}
